import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import JsonLd from "@/components/feature/JsonLd";
import useScrollReveal from "@/hooks/useScrollReveal";
import CareHero from "@/components/feature/care/CareHero";
import CareIntro from "@/components/feature/care/CareIntro";
import CarePanels from "@/components/feature/care/CarePanels";
import CareSymptoms from "@/components/feature/care/CareSymptoms";
import CareSteps from "@/components/feature/care/CareSteps";
import CareDoctor from "@/components/feature/care/CareDoctor";
import CareFaq from "@/components/feature/care/CareFaq";
import CareCta from "@/components/feature/care/CareCta";
import BodyMap, { type BodyRegion } from "@/components/feature/care/BodyMap";
import { doctors } from "@/data/doctors";
import type { Faq } from "@/types/content";

const careAreas = [
  {
    title: "Joint Care",
    text: "Assessment and management of joint pain and stiffness, including joint replacement surgery for arthritis when other options no longer provide relief.",
    image:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20a%20human%20knee%20joint%20with%20highlighted%20cartilage%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%20and%20violet%20lighting%2C%20premium%20medical%20education%20visual%2C%20no%20people&width=800&height=600&seq=ospc-ortho-area-joint-2026&orientation=landscape",
    imageAlt: "Anatomical illustration of the knee joint, illustrating joint care in Trichy",
    to: "/services/joint-disorders",
  },
  {
    title: "Knee Care",
    text: "From ligament injuries and sports trauma to arthritis of the knee, with arthroscopy and knee replacement offered where they are appropriate.",
    image:
      "https://readdy.ai/api/search-image?query=Anatomical%20knee%20joint%20model%20resting%20on%20a%20clean%20light%20desk%20in%20a%20modern%20clinic%20with%20a%20soft%20neutral%20background%2C%20medical%20education%20photography%20with%20crisp%20detail%20and%20warm%20studio%20lighting&width=800&height=600&seq=ospc-ortho-area-knee-2026&orientation=landscape",
    imageAlt: "Anatomical knee model used to explain knee care and treatment",
    to: "/services/joint-disorders",
  },
  {
    title: "Shoulder Care",
    text: "Care for shoulder pain and rotator cuff injuries of the shoulder, including tendon repair and structured rehabilitation.",
    image:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20the%20shoulder%20joint%20and%20rotator%20cuff%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%20and%20violet%20lighting%2C%20premium%20medical%20education%20visual%2C%20no%20people&width=800&height=600&seq=ospc-ortho-area-shoulder-2026&orientation=landscape",
    imageAlt: "Anatomical illustration of the shoulder joint and rotator cuff",
    to: "/services/tendon-disorders",
  },
  {
    title: "Bone & Fracture Care",
    text: "Management of simple to complex bone fractures, focused on restoring alignment so the bone can heal and function can return.",
    image:
      "https://readdy.ai/api/search-image?query=Orthopaedic%20X%20ray%20image%20of%20an%20arm%20fracture%20displayed%20on%20a%20modern%20lightbox%20in%20a%20clean%20clinic%2C%20neutral%20background%2C%20professional%20medical%20photography%20with%20soft%20even%20lighting%20and%20sharp%20detail&width=800&height=600&seq=ospc-ortho-area-fracture-2026&orientation=landscape",
    imageAlt: "X-ray of a bone fracture on a lightbox for orthopaedic assessment",
    to: "/services/complex-bone-fracture-management",
  },
  {
    title: "Sports Injury Care",
    text: "Ligament and tendon injuries from sport and physical activity, with early assessment and a guided path back to activity.",
    image:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20illustration%20of%20knee%20ligaments%20and%20muscles%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%20and%20violet%20lighting%2C%20premium%20medical%20education%20visual%2C%20no%20people&width=800&height=600&seq=ospc-ortho-area-sports-2026&orientation=landscape",
    imageAlt: "Anatomical illustration of knee ligaments and muscles for sports injury care",
    to: "/services/tendon-disorders",
  },
  {
    title: "Arthritis Care",
    text: "Care for joint pain, stiffness and deformity caused by arthritis, aimed at controlling pain and maintaining everyday function.",
    image:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20an%20arthritic%20joint%20with%20cartilage%20wear%20highlighted%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%20and%20violet%20lighting%2C%20premium%20medical%20education%20visual%2C%20no%20people&width=800&height=600&seq=ospc-ortho-area-arthritis-2026&orientation=landscape",
    imageAlt: "Anatomical illustration of an arthritic joint with cartilage wear",
    to: "/services/joint-disorders",
  },
];

const bodyRegions: BodyRegion[] = [
  {
    label: "Spine",
    icon: "ri-body-scan-line",
    text: "Neck and back problems, disc issues and spinal stenosis.",
    to: "/services/spine-care",
  },
  {
    label: "Shoulder",
    icon: "ri-user-3-line",
    text: "Rotator cuff injuries, shoulder pain and instability.",
    to: "/services/tendon-disorders",
  },
  {
    label: "Elbow",
    icon: "ri-hand-coin-line",
    text: "Elbow pain and injuries from sport or overuse.",
  },
  {
    label: "Wrist & Hand",
    icon: "ri-hand-heart-line",
    text: "Wrist and hand tendon injuries and soft tissue problems.",
    to: "/services/tendon-disorders",
  },
  {
    label: "Hip",
    icon: "ri-heart-2-line",
    text: "Hip joint pain and arthritis care, including joint replacement.",
    to: "/services/joint-disorders",
  },
  {
    label: "Knee",
    icon: "ri-focus-3-line",
    text: "Knee ligament injuries, sports trauma and arthritis.",
    to: "/services/joint-disorders",
  },
  {
    label: "Ankle & Foot",
    icon: "ri-footprint-line",
    text: "Fractures, heel tendon injuries and ankle trauma.",
    to: "/services/complex-bone-fracture-management",
  },
];

const symptoms = [
  { icon: "ri-heart-pulse-line", title: "Persistent joint pain" },
  { icon: "ri-restart-line", title: "Difficulty moving a joint" },
  { icon: "ri-first-aid-kit-line", title: "Injury or trauma" },
  { icon: "ri-drop-line", title: "Swelling around a joint" },
  { icon: "ri-run-line", title: "Repeated sports injuries" },
  { icon: "ri-shield-cross-line", title: "Bone or joint problems" },
  { icon: "ri-time-line", title: "Morning stiffness" },
  { icon: "ri-close-circle-line", title: "Pain limiting daily activity" },
];

const journey = [
  {
    step: "01",
    title: "Consultation",
    text: "Discuss your symptoms, history and goals with our specialist.",
    icon: "ri-chat-3-line",
  },
  {
    step: "02",
    title: "Evaluation",
    text: "A clinical examination of movement, strength and stability.",
    icon: "ri-stethoscope-line",
  },
  {
    step: "03",
    title: "Diagnosis",
    text: "Appropriate imaging such as X-ray or MRI to confirm the cause.",
    icon: "ri-scan-2-line",
  },
  {
    step: "04",
    title: "Personalised Care",
    text: "A plan built around you, considering non-surgical options first.",
    icon: "ri-clipboard-line",
  },
  {
    step: "05",
    title: "Follow-Up",
    text: "Structured rehabilitation and follow-up through your recovery.",
    icon: "ri-heart-2-line",
  },
];

const orthoFaqs: Faq[] = [
  {
    q: "When should I see an orthopaedic specialist?",
    a: "Consider a specialist opinion if pain, swelling or stiffness in a bone or joint lasts more than a few weeks, limits daily activities, follows an injury, or comes with numbness or weakness. Early assessment often allows simpler treatment options.",
  },
  {
    q: "What does orthopaedic care cover?",
    a: "Orthopaedic care covers the bones, joints, muscles and tendons that allow you to move. This includes arthritis, sports injuries, fractures, tendon injuries, bone infections, bone deformities and joint replacement.",
  },
  {
    q: "Do all joint problems need surgery?",
    a: "No. Most joint conditions are managed first with activity guidance, physiotherapy and medication. Surgery such as arthroscopy or joint replacement is considered when it offers a clear benefit for your condition.",
  },
  {
    q: "How are sports injuries treated?",
    a: "Treatment depends on the injury. Many ligament and tendon injuries improve with rest and physiotherapy, while more significant injuries may need arthroscopy or tendon repair. Early assessment helps plan a safe return to activity.",
  },
  {
    q: "How can I book an appointment?",
    a: "You can request an appointment through the booking form on this website, or call the clinic on +91 99433 16050. Our team will confirm a suitable time with you.",
  },
];

export default function OrthopaedicCare() {
  useScrollReveal();

  return (
    <Layout>
      <Seo
        title="Orthopaedic Care in Trichy | Orthopaedic Specialist & Joint Care"
        description="Orthopaedic care in Srirangam, Trichy for joint replacement, arthroscopy, fracture management, bone infections, sports injuries and tendon problems."
        path="/services/orthopaedic-care"
      />
      <JsonLd
        id="ld-ortho"
        data={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "MedicalWebPage",
              name: "Orthopaedic Care",
              url: "https://orthospine.care/services/orthopaedic-care",
              about: { "@type": "MedicalCondition", name: "Orthopaedic conditions" },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://orthospine.care/" },
                { "@type": "ListItem", position: 2, name: "Services", item: "https://orthospine.care/services" },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "Orthopaedic Care",
                  item: "https://orthospine.care/services/orthopaedic-care",
                },
              ],
            },
          ],
        }}
      />

      <CareHero
        eyebrow="Orthopaedic Care"
        title={
          <>
            Comprehensive Care for Bones &amp;
            <span className="italic text-primary-700"> Joints</span>
          </>
        }
        intro="Specialist care for the bones, joints, muscles and tendons that keep you moving — from arthritis and sports injuries to complex fractures, in Srirangam, Trichy."
        image={doctors[0].image}
        imageAlt={doctors[0].imageAlt}
        badgeIcon="ri-focus-3-line"
        badgeTitle="Joint Replacement"
        badgeText="Arthroscopy & trauma care"
        chips={["Joint Replacement", "Arthroscopy", "Fracture Care", "Sports Injuries", "Arthritis", "Bone Infections"]}
        chipsLabel="Orthopaedic care we provide"
        crumbs={[{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: "Orthopaedic Care" }]}
      />

      <CareIntro
        eyebrow="Orthopaedic care"
        title="Comprehensive Orthopaedic Care"
        paragraphs={[
          "Orthopaedic conditions affect the bones, joints, muscles and tendons that allow us to move. Whether the problem is arthritis, a sports injury, a fracture or a bone infection, an accurate diagnosis is the first step to the right treatment.",
          "We carefully consider non-surgical options first, including activity guidance, physiotherapy and medication, and reserve surgery for when it is genuinely needed, using arthroscopy and joint replacement where appropriate.",
        ]}
        points={[
          "Non-surgical options considered first wherever suitable",
          "Arthroscopy and joint replacement when they are needed",
          "Care for complex trauma and difficult fractures",
        ]}
        image="https://readdy.ai/api/search-image?query=Anatomical%20knee%20joint%20model%20and%20knee%20X%20ray%20films%20on%20a%20clean%20consultation%20desk%20in%20a%20bright%20modern%20clinic%2C%20soft%20royal%20blue%20and%20violet%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography%2C%20no%20people&width=1200&height=800&seq=ospc-ortho-intro-2026&orientation=landscape"
        imageAlt="Anatomical knee model and X-ray films used to explain orthopaedic care in Trichy"
        ctaLabel="Explore Our Services"
        ctaTo="/services"
        ctaIcon="ri-stethoscope-line"
        reverse
      />

      <CarePanels
        eyebrow="Orthopaedic care areas"
        title="The areas we look after"
        description="Each area is assessed individually. Understanding the cause is the first step towards care that is right for you."
        panels={careAreas}
        tone="tinted"
      />

      <BodyMap
        eyebrow="Where we treat"
        title="Orthopaedic Care From Head to Toe"
        description="Our practice covers the major joints and regions of the body. Select an area to see the relevant service, or ask us if you are unsure."
        image="https://readdy.ai/api/search-image?query=Clean%20full%20body%20anatomical%20illustration%20of%20the%20human%20musculoskeletal%20system%20highlighting%20spine%20shoulder%20elbow%20wrist%20hip%20knee%20and%20ankle%20on%20a%20soft%20neutral%20background%2C%20medical%20education%20visual%20with%20teal%20and%20navy%20tones&width=900&height=1100&seq=ospc-ortho-bodymap-2026&orientation=portrait"
        imageAlt="Anatomical illustration of the human musculoskeletal system"
        regions={bodyRegions}
      />

      <CareSymptoms
        eyebrow="When to seek care"
        title="When Should You See an Orthopaedic Specialist?"
        description="Some bone and joint problems settle with time. The signs below are a good reason to arrange an assessment, so the cause can be understood and treated early."
        items={symptoms}
        image="https://readdy.ai/api/search-image?query=Anatomical%20shoulder%20joint%20model%20on%20a%20clean%20desk%20in%20a%20bright%20modern%20clinic%2C%20soft%20royal%20blue%20and%20violet%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography%2C%20no%20people&width=900&height=1200&seq=ospc-ortho-symptoms-2026&orientation=portrait"
        imageAlt="Anatomical shoulder joint model used during an orthopaedic assessment"
        ctaLabel="Book a Consultation"
        tone="tinted"
      />

      <CareSteps
        eyebrow="Your patient journey"
        title="What to Expect, Step by Step"
        description="From your first conversation to your follow-up, care is structured and clear so you always know what happens next."
        steps={journey}
        tone="light"
      />

      <CareDoctor
        eyebrow="Meet your specialist"
        title="Meet Your Orthopaedic Specialist"
        ctaLabel="Book a Consultation"
      />

      <CareFaq
        eyebrow="Orthopaedic FAQ"
        title="Orthopaedic care questions, answered"
        items={orthoFaqs}
        idPrefix="ortho"
        tone="light"
      />

      <CareCta
        title="Get an expert opinion on your joint or bone pain"
        text="Early assessment often means simpler treatment. Book a consultation with our orthopaedic specialist to understand your options."
        image="https://readdy.ai/api/search-image?query=Modern%20bright%20orthopaedic%20clinic%20corridor%20with%20soft%20warm%20natural%20light%20and%20neutral%20tones%2C%20premium%20healthcare%20interior%20photography%2C%20calm%20clean%20and%20welcoming%20atmosphere&width=1600&height=900&seq=ospc-ortho-cta-2026&orientation=landscape"
      />
    </Layout>
  );
}